import { api, type SourceInfo, type StatusSnapshot } from '../api';
import { editionLabel } from '../editions';
import { Chip, ErrorState, Loading, Notice, PageIntro, StatusChip, useAsync } from '../components/ui';
import { formatDateTime, formatLocalDayShort, plural, relativeAge } from '../format';

/**
 * Datenstatus: what is present, what is missing and why.
 *
 * There is no overall green light. Each area states what it knows; a source is
 * "aktuell" only if its last run succeeded and it is not stale, and every gap
 * names its cause as far as the platform recorded one.
 */

type SourceRow = StatusSnapshot['sources'][number] & { info?: SourceInfo };

function sourceHealth(s: SourceRow): 'ok' | 'stale' | 'partial' | 'failed' | 'never' | 'disabled' {
  if (!s.enabled) return 'disabled';
  if (!s.last_run_at && !s.last_success_at) return 'never';
  if (s.last_status === 'failed') return 'failed';
  if (s.stale) return 'stale';
  if (s.last_status === 'partial') return 'partial';
  return 'ok';
}

const HEALTH_LABEL: Record<ReturnType<typeof sourceHealth>, { kind: string; label: string }> = {
  ok: { kind: 'ok', label: 'aktuell' },
  stale: { kind: 'warn', label: 'veraltet' },
  partial: { kind: 'warn', label: 'teilweise' },
  failed: { kind: 'danger', label: 'fehlgeschlagen' },
  never: { kind: 'danger', label: 'noch nie gelesen' },
  disabled: { kind: 'neutral', label: 'deaktiviert' },
};

/** Technical diagnostics recorded for a source's latest run. */
function diagnostics(s: SourceRow): string[] {
  const out: string[] = [];
  if (s.last_error) out.push(s.last_error);
  for (const w of s.info?.last_run?.warnings ?? []) if (!out.includes(w)) out.push(w);
  return out;
}

/** A readable cause; the collector's own wording stays available in a disclosure. */
function gapCause(s: SourceRow): string | null {
  const h = sourceHealth(s);
  if (h === 'ok' || h === 'disabled') return null;
  if (h === 'never') return 'Für diese Quelle wurde noch kein Lauf aufgezeichnet.';
  const d = diagnostics(s);
  const parts: string[] = [];
  if (h === 'stale') parts.push(`Seit ${plural(Math.round(s.stale_minutes / 60), 'Stunde', 'Stunden')} kein erfolgreicher Abruf.`);
  const fullText = d.filter((w) => /full text not obtained|body could not be retrieved|extracted body/.test(w)).length;
  if (fullText > 0) {
    const paywall = d.some((w) => /paywall|withheld/.test(w));
    parts.push(
      `Bei ${plural(fullText, 'Artikel', 'Artikeln')} wurde nur ein Teil des Textes gelesen${paywall ? ' (Bezahlschranke oder nachgeladener Text)' : ''}; die Meldungen beruhen dort auf dem Anriss.`,
    );
  }
  const other = d.length - fullText;
  if (h === 'failed') parts.push(other > 0 || d.length > 0 ? 'Der letzte Abruf ist fehlgeschlagen.' : 'Der letzte Abruf ist fehlgeschlagen; keine Ursache aufgezeichnet.');
  else if (other > 0) parts.push(`${plural(other, 'weiterer Hinweis', 'weitere Hinweise')} des Abrufs.`);
  if (parts.length === 0) parts.push('Keine Ursache aufgezeichnet.');
  return parts.join(' ');
}

async function load() {
  const [status, sources] = await Promise.all([api.status(), api.sources().catch(() => ({ sources: [] as SourceInfo[] }))]);
  return { status, sources: sources.sources };
}

export function StatusPage() {
  const { data, error, loading, reload } = useAsync(load, []);
  return (
    <>
      <PageIntro eyebrow="Herkunft & Aktualität" title="Wissen, worauf du dich verlässt.">
        Welche Informationen vorliegen, wann sie erfasst wurden und wo etwas fehlt – mit der Ursache, soweit bekannt.
      </PageIntro>
      <div className="toolbar">
        <p className="muted small">{data ? `Stand ${formatDateTime(data.status.generated_at)} · ${data.status.timezone}` : ''}</p>
        <button type="button" className="small-button" onClick={reload} disabled={loading}>
          {loading ? 'Aktualisiert…' : 'Aktualisieren'}
        </button>
      </div>
      {loading && !data && <Loading />}
      {error && <ErrorState error={error} onRetry={reload} />}
      {data && <StatusBody s={data.status} catalogue={data.sources} />}
    </>
  );
}

function StatusBody({ s, catalogue }: { s: StatusSnapshot; catalogue: SourceInfo[] }) {
  const info = new Map(catalogue.map((c) => [c.id, c]));
  const rows: SourceRow[] = s.sources.map((src) => ({ ...src, info: info.get(src.source_id) }));
  const news = rows.filter((r) => (r.info?.section ?? 'news') !== 'crossfit' && r.enabled);
  const training = rows.filter((r) => r.info?.section === 'crossfit' || r.source_id === 'wodify');
  const newsOk = news.filter((r) => sourceHealth(r) === 'ok').length;
  // Slots still ahead (or prepared, not yet visible) are neither late nor missed.
  const pending = (e: (typeof s.editions)[number]) => e.state === 'upcoming' || e.state === 'due';
  const lastEdition = s.editions.find((e) => e.published_at && !pending(e)) ?? null;
  const missedEditions = s.editions.filter((e) => !pending(e) && (!e.published_at || !e.on_time)).length;
  const badComponents = s.components.filter((c) => c.status !== 'ok');

  return (
    <>
      {s.warnings.length > 0 && (
        <Notice kind="warn" title={plural(s.warnings.length, 'Hinweis', 'Hinweise')}>
          <ul>
            {s.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Notice>
      )}

      <div className="status-grid">
        <section className="panel">
          <span className="eyebrow">Nachrichten</span>
          <p className="status-value">
            {news.length === 0 ? 'Keine Quellen' : newsOk === news.length ? `Alle ${news.length} aktuell` : `${newsOk} von ${news.length} aktuell`}
          </p>
          <p className="muted">
            {news.length - newsOk > 0
              ? `${plural(news.length - newsOk, 'Quelle hat', 'Quellen haben')} eine Lücke; Ursachen stehen unten.`
              : 'Jede aktive Quelle wurde zuletzt vollständig gelesen.'}
          </p>
        </section>
        <section className="panel">
          <span className="eyebrow">CrossFit</span>
          {training.length === 0 ? (
            <p className="status-value">Nicht eingerichtet</p>
          ) : (
            training.map((t) => (
              <div key={t.source_id}>
                <p className="status-value">{HEALTH_LABEL[sourceHealth(t)].label.replace(/^./, (c) => c.toUpperCase())}</p>
                <p className="muted">
                  {t.last_success_at ? `Zuletzt erfolgreich ${relativeAge(t.last_success_at).replace(/\.$/, '')}.` : 'Noch keine erfolgreiche Lesung.'}{' '}
                  {gapCause(t) ?? ''}
                </p>
              </div>
            ))
          )}
        </section>
        <section className="panel">
          <span className="eyebrow">Ausgaben</span>
          <p className="status-value">
            {lastEdition
              ? `${editionLabel(lastEdition.edition_type, lastEdition.label)} ${formatLocalDayShort(lastEdition.edition_date)}`
              : 'Noch keine'}
          </p>
          <p className="muted">
            {lastEdition
              ? `${lastEdition.on_time ? 'Pünktlich' : 'Verspätet'} veröffentlicht, ${plural(lastEdition.entries, 'Karte', 'Karten')}. `
              : ''}
            {missedEditions > 0
              ? `${plural(missedEditions, 'Ausgabe', 'Ausgaben')} der letzten sieben Tage verspätet oder nicht erschienen.`
              : s.editions.length > 0
                ? 'Alle Ausgaben der letzten sieben Tage pünktlich.'
                : ''}{' '}
            Täglich 06:00, 09:00, 10:00, 12:00, 15:00, 18:00 und 20:00 (Europe/Zurich).
          </p>
        </section>
        <section className="panel">
          <span className="eyebrow">Verarbeitung</span>
          <p className="status-value">
            {badComponents.length === 0 && s.jobs.open_failures === 0 ? 'Keine Störung gemeldet' : `${badComponents.length + (s.jobs.open_failures > 0 ? 1 : 0)} Auffälligkeiten`}
          </p>
          <p className="muted">
            {badComponents.map((c) => `${c.name}: ${c.status === 'down' ? 'ausgefallen' : 'eingeschränkt'}${c.detail ? ` (${c.detail})` : ''}`).join(' · ')}
            {badComponents.length > 0 ? '. ' : ''}
            {s.jobs.open_failures > 0 ? `${plural(s.jobs.open_failures, 'offener Fehler', 'offene Fehler')} in der Verarbeitung. ` : ''}
            {s.jobs.outbox_backlog > 0 ? `${s.jobs.outbox_backlog} Ereignisse warten auf Zustellung.` : 'Keine wartenden Ereignisse.'}
          </p>
        </section>
      </div>

      <section className="status-section" aria-labelledby="src-h">
        <h2 id="src-h">Quellen</h2>
        <ul className="source-status">
          {rows.map((src) => {
            const h = sourceHealth(src);
            const cause = gapCause(src);
            const run = src.info?.last_run;
            return (
              <li key={src.source_id} className={`health-${h}`}>
                <div className="spread">
                  <strong>{src.name}</strong>
                  <span className="row">
                    <Chip kind={HEALTH_LABEL[h].kind}>{HEALTH_LABEL[h].label}</Chip>
                    {src.last_status && h !== 'ok' && <StatusChip status={src.last_status} />}
                  </span>
                </div>
                <p className="muted small">
                  {src.last_success_at ? `Zuletzt erfolgreich ${relativeAge(src.last_success_at)} (${formatDateTime(src.last_success_at)})` : 'Noch kein erfolgreicher Abruf'}
                  {run ? ` · letzter Lauf ${relativeAge(run.completed_at)}, ${plural(run.item_count, 'Eintrag', 'Einträge')}` : ''}
                </p>
                {cause && <p className="gap-cause small">{cause}</p>}
                {cause && diagnostics(src).length > 0 && (
                  <details className="diag">
                    <summary>Technische Ursache</summary>
                    <ul className="plain small" lang="en">
                      {diagnostics(src).map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="status-section" aria-labelledby="ed-h">
        <h2 id="ed-h">Ausgaben der letzten sieben Tage</h2>
        {s.editions.length === 0 ? (
          <p className="muted">Keine Ausgaben in diesem Zeitraum.</p>
        ) : (
          <ul className="source-status">
            {s.editions.map((e) => (
              <li key={`${e.edition_date}-${e.edition_type}`} className="spread">
                <span>
                  {formatLocalDayShort(e.edition_date)} · {editionLabel(e.edition_type, e.label)}
                  <span className="muted small"> (Ziel {e.deadline})</span>
                </span>
                <span className="row">
                  {!pending(e) && <span className="muted small">{plural(e.entries, 'Karte', 'Karten')}</span>}
                  {e.quiet ? <Chip kind="neutral">ruhig</Chip> : null}
                  {pending(e) ? (
                    <Chip kind="neutral">{e.state === 'due' ? 'in Vorbereitung' : 'folgt'}</Chip>
                  ) : e.published_at ? (
                    <Chip kind={e.on_time ? 'ok' : 'warn'}>{e.on_time ? 'pünktlich' : 'verspätet'}</Chip>
                  ) : (
                    <Chip kind="danger">nicht erschienen</Chip>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <details className="disclosure">
        <summary>Technische Details</summary>
        <div className="status-grid">
          <section>
            <h3 className="mini-h">Komponenten</h3>
            <ul className="plain small">
              {s.components.map((c) => (
                <li key={c.name}>
                  {c.name}: {c.status}
                  {c.detail ? ` · ${c.detail}` : ''}
                </li>
              ))}
            </ul>
            {(s.jobs.failures ?? []).map((f) => (
              <Notice kind="danger" key={f.id} title={`${f.consumer} · ${f.event_type}`}>
                {f.error} ({formatDateTime(f.last_failed_at)})
              </Notice>
            ))}
          </section>
          <section>
            <h3 className="mini-h">Speicher &amp; Benachrichtigungen</h3>
            <dl className="kv">
              <dt>Speicher</dt>
              <dd>
                {s.storage.backend}, {s.storage.files} Dateien, {(s.storage.bytes / 1024 / 1024).toFixed(1)} MB
              </dd>
              <dt>Gesendet (24 h)</dt>
              <dd>{s.notifications.sent_24h}</dd>
              <dt>Unterdrückt</dt>
              <dd>{s.notifications.suppressed_24h}</dd>
              <dt>Unklar</dt>
              <dd>{s.notifications.unknown_24h}</dd>
              <dt>Fehlgeschlagen</dt>
              <dd>{s.notifications.failed_24h}</dd>
              <dt>Laufzeit</dt>
              <dd>{Math.round(s.uptime_seconds / 3600)} h</dd>
            </dl>
          </section>
        </div>
      </details>
    </>
  );
}
